/* eslint-disable max-lines */
import { AlertsDetailFill, InfoFill, SuccessFill, WarningSquare } from '@elliemae/ds-icons';
import { useOwnerProps } from '@elliemae/ds-props-helpers';
import { TYPOGRAPHY_VARIANTS } from '@elliemae/ds-typography';
import React, { useCallback } from 'react';
import { BANNER_BODY_ID, DSBannerDatatestid } from './exported-related/index.js';
import type { DSBannerT } from './react-desc-prop-types.js';
import { StyledActionLink, StyledIconContainer, StyledSubTitle, StyledTitle } from './styles.js';

export const icons: Record<string, JSX.Element> = {
  success: <SuccessFill color={['success', '900']} width="20px" height="20px" />,
  info: <InfoFill color={['brand-primary', '600']} width="20px" height="20px" />,
  danger: <AlertsDetailFill color={['danger', '900']} width="20px" height="20px" />,
  warning: <WarningSquare color={['warning', '900']} width="20px" height="20px" />,
};

interface DSBannerContentProps {
  propsWithDefaults: DSBannerT.InternalProps;
}

export const DSBannerContent: React.ComponentType<DSBannerContentProps> = (props) => {
  const {
    propsWithDefaults,
    propsWithDefaults: { type, label, body, actionLink, actionRef },
  } = props;
  const ownerPropsConfig = useOwnerProps(propsWithDefaults, { ...props });

  const linkRef = React.useRef<HTMLAnchorElement>(null) as React.MutableRefObject<HTMLAnchorElement>;

  const handleActionRefForLink = useCallback<React.RefCallback<HTMLAnchorElement>>(
    (linkNode) => {
      if (linkNode) {
        linkRef.current = linkNode;
        if (actionRef && actionRef.current) {
          actionRef.current.focusOnLink = () => {
            linkNode?.focus?.();
          };
        }
      }
    },
    [actionRef],
  );

  const handleOnKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (linkRef.current) linkRef.current.click();
    }
  }, []);

  return (
    <>
      <StyledIconContainer data-testid={DSBannerDatatestid.ICON_CONTAINER} {...ownerPropsConfig}>
        {icons[type]}
      </StyledIconContainer>
      <StyledTitle variant={TYPOGRAPHY_VARIANTS.B1} {...ownerPropsConfig}>
        {label}
      </StyledTitle>
      <StyledSubTitle id={BANNER_BODY_ID} variant={TYPOGRAPHY_VARIANTS.B1} {...ownerPropsConfig}>
        {body}
        {actionLink && (
          <>
            {/* This way of handling the space is making use of the browser default
              white-space clamping, avoiding lots of JS code. */}
            {!body ? '' : '    '}
            <StyledActionLink
              data-testid={DSBannerDatatestid.ACTION_LINK}
              onClick={actionLink.onClick}
              href={actionLink.href}
              innerRef={handleActionRefForLink}
              onKeyDown={handleOnKeyDown}
              tabIndex={0}
              isBodyEmpty={!body}
              title={actionLink.label}
              {...ownerPropsConfig}
            >
              {actionLink.label}
            </StyledActionLink>
          </>
        )}
      </StyledSubTitle>
    </>
  );
};
